import React, { useState } from "react";
import { FlexLayout } from "@design-system/FlexLayout/FlexLayout";
import type {
    LayoutAlign,
    LayoutJustify,
    SpaceStep,
} from "@design-system/FlexLayout/layout.types";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import {
    BooleanControl,
    SelectControl,
    VariantGrid,
    VariantItem,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./structureLayoutControls.module.scss";

const gapOptions = ["4", "8", "16", "24", "32"] as const;
type GapOption = (typeof gapOptions)[number];

const gaps: Record<GapOption, SpaceStep> = {
    "4": 4,
    "8": 8,
    "16": 16,
    "24": 24,
    "32": 32,
};

const alignments = [
    "start",
    "center",
    "end",
    "stretch",
] as const satisfies readonly LayoutAlign[];
const justifications = [
    "start",
    "center",
    "end",
    "between",
    "around",
    "evenly",
] as const satisfies readonly LayoutJustify[];
const directions = ["row", "column"] as const;
type Direction = (typeof directions)[number];

const layoutItems = ["One", "Two", "Three"];

const LayoutBoxes = () => (
    <>
        {layoutItems.map((item) => (
            <div key={item} className={styles.layoutBox}>
                {item}
            </div>
        ))}
    </>
);

export const StacksPlayground = () => {
    const [gap, setGap] = useState<GapOption>("16");
    const [align, setAlign] = useState<LayoutAlign>("center");
    const [justify, setJustify] = useState<LayoutJustify>("between");
    const [direction, setDirection] = useState<Direction>("row");
    const [shouldWrap, setShouldWrap] = useState(false);

    const sharedProps = {
        gap: gaps[gap],
        align,
        justify,
        wrap: shouldWrap,
    };

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Layout</h2>
                        <div className={styles.fields}>
                            <SelectControl
                                label="Gap"
                                options={gapOptions}
                                value={gap}
                                onChange={setGap}
                            />
                            <SelectControl
                                label="Align"
                                options={alignments}
                                value={align}
                                onChange={setAlign}
                            />
                            <SelectControl
                                label="Justify"
                                options={justifications}
                                value={justify}
                                onChange={setJustify}
                            />
                            <SelectControl
                                label="Flex direction"
                                options={directions}
                                value={direction}
                                onChange={setDirection}
                            />
                        </div>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Wrap"
                                checked={shouldWrap}
                                onChange={setShouldWrap}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${gap}px · ${align} · ${justify}`}
                alignment="start"
                width="full"
                output={JSON.stringify(
                    {
                        componentProps: {
                            gap: gaps[gap],
                            align,
                            justify,
                            wrap: shouldWrap,
                            flexDirection: direction,
                        },
                        showcaseState: {},
                    },
                    null,
                    2,
                )}
            >
                <VariantGrid title="Layout primitives" columns="full">
                    <VariantItem label="HStack">
                        <HStack className={styles.layoutStage} {...sharedProps}>
                            <LayoutBoxes />
                        </HStack>
                    </VariantItem>
                    <VariantItem label="VStack">
                        <VStack className={styles.layoutStage} {...sharedProps}>
                            <LayoutBoxes />
                        </VStack>
                    </VariantItem>
                    <VariantItem label={`FlexLayout · ${direction}`}>
                        <FlexLayout
                            className={styles.layoutStage}
                            direction={direction}
                            {...sharedProps}
                        >
                            <LayoutBoxes />
                        </FlexLayout>
                    </VariantItem>
                </VariantGrid>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
